import React, { FC, useMemo, useRef, useState } from 'react';

import { type Loan } from '@column/api';
import { Icon } from '@column/column-ui-kit';
import { Popover, PopoverContent, PopoverTrigger, formatAmount } from '@column/ui';

import { useLoanOptions } from '~/components/fields/LoanDropdown';
import { type HighlightSuffixCandidate, getHighlightedSuffix } from '~/lib/search/highlight';

import {
  FADE_ONLY_MOTION,
  FadeSwap,
  OVERLAP_CELL,
  OVERLAP_GRID,
  POPOVER_CONTENT_CLASS,
  SelectedCard,
  type SelectedCardRow,
  type SelectorOption,
  SelectorSearch,
  SelectorTrigger,
  TRIGGER_HEIGHT_PX,
  formatStatus,
  highlightMatch,
  renderMatchFragment,
} from './primitives';

const HIDDEN_MATCH_CANDIDATES: HighlightSuffixCandidate[] = [
  { field: 'PRIMARY_SIGNER_ENTITY_NAME', label: 'SIGNER' },
  { field: 'PRIMARY_SIGNER_ENTITY_ID', label: 'SIGNER ID' },
  { field: 'LOAN_ID', label: 'ID' },
];

const AVATAR_ICON_CLASS = '[--icon-size-x:20px] [--icon-size-y:20px]';

const loanLabel = (loan: Loan) => loan.description || loan.id;

export const formatLoanKind = (loan: Loan) =>
  [loan.type?.toLowerCase() === 'secured' ? 'Secured' : 'Unsecured', loan.is_revolving && 'Revolving']
    .filter(Boolean)
    .join(' · ');

export const getOutstandingPrincipal = (loan: Loan) => Number(loan.balances?.principal_outstanding ?? 0) || 0;

export const getInterestReceivable = (loan: Loan) => Number(loan.balances?.interest_receivable ?? 0) || 0;

/**
 * Principal still available to disburse, in minor units. Mirrors the API's check, which counts held disbursements
 * as though they'll clear.
 */
export const getAvailablePrincipal = (loan: Loan) =>
  Math.max(
    0,
    loan.max_principal_balance - getOutstandingPrincipal(loan) - Number(loan.balances?.principal_holding ?? 0)
  );

export const loanIdRow = (loan: Loan): SelectedCardRow => ({ label: 'Loan ID', value: loan.id, monospaced: true });

const defaultRows = (loan: Loan): SelectedCardRow[] => [
  {
    label: 'Outstanding principal',
    value: formatAmount(getOutstandingPrincipal(loan), loan.currency),
    emphasized: true,
  },
  { label: 'Max principal balance', value: formatAmount(loan.max_principal_balance, loan.currency) },
  { label: 'Status', value: formatStatus(loan.status) },
  loanIdRow(loan),
];

const defaultBalance = (loan: Loan) => ({ amount: getOutstandingPrincipal(loan), label: 'Outstanding' });

export interface LoanSelectorProps {
  value: string;
  onChange: (id: string) => void;
  onBlur: () => void;
  locked?: boolean;
  hasError?: boolean;
  /** Rows on the selected loan's card. */
  getRows?: (loan: Loan) => SelectedCardRow[];
  /** Balance shown beside each option, in minor units. */
  getBalance?: (loan: Loan) => { amount: number; label: string };
  dataCy?: string;
}

export const LoanSelector: FC<LoanSelectorProps> = ({
  value,
  onChange,
  onBlur,
  locked,
  hasError,
  getRows = defaultRows,
  getBalance = defaultBalance,
  dataCy,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selectedOnClose = useRef(false);
  const { loans, activeLoan, activeLoanIsLoading, isLoanSearchEnabled, search, handleScrollEnd } = useLoanOptions({
    value,
  });

  const options: SelectorOption[] = useMemo(() => {
    const needle = query.trim().toLowerCase();
    // Without server search, narrow the loaded page on the client instead.
    const visible =
      isLoanSearchEnabled || !needle
        ? loans
        : loans.filter((option) => `${option.description} ${option.id}`.toLowerCase().includes(needle));

    return visible.map((option) => {
      const matchedHidden = getHighlightedSuffix(option.search_metadata, HIDDEN_MATCH_CANDIDATES);
      const balance = getBalance(option);
      return {
        id: option.id,
        primary: highlightMatch(loanLabel(option), query),
        secondary: matchedHidden ? (
          <span className="font-mono">
            <span className="text-muted-foreground/60">{matchedHidden.label} </span>
            {renderMatchFragment(matchedHidden.fragment)}
          </span>
        ) : (
          formatLoanKind(option)
        ),
        trailingPrimary: formatAmount(balance.amount, option.currency),
        trailingSecondary: balance.label,
      };
    });
  }, [loans, query, isLoanSearchEnabled, getBalance]);

  const loan = activeLoan ?? loans.find((l) => l.id === value);

  const closePopover = () => {
    setOpen(false);
    setQuery('');
    search('');
  };

  return (
    <div className={OVERLAP_GRID} data-cy={dataCy}>
      <SelectedCard
        className={OVERLAP_CELL}
        open={Boolean(value)}
        title={loan ? loanLabel(loan) : 'Loan'}
        subtitle={loan ? formatLoanKind(loan) : undefined}
        icon={<Icon.HandCoins className={AVATAR_ICON_CLASS} />}
        rows={loan ? getRows(loan) : []}
        isLoading={!loan && activeLoanIsLoading}
        hasError={hasError}
        onClear={locked ? undefined : () => onChange('')}
      />
      <FadeSwap visible={!value} className={OVERLAP_CELL}>
        <Popover
          open={open}
          onOpenChange={(next) => {
            setOpen(next);
            if (!next) {
              onBlur();
              setQuery('');
              search('');
            }
          }}
        >
          <PopoverTrigger asChild>
            <SelectorTrigger label="Select a loan" hasError={hasError} />
          </PopoverTrigger>
          <PopoverContent
            align="start"
            sideOffset={-TRIGGER_HEIGHT_PX}
            className={POPOVER_CONTENT_CLASS}
            {...FADE_ONLY_MOTION}
            onCloseAutoFocus={(event) => {
              // After a selection the trigger is hidden, so don't restore focus to it.
              if (selectedOnClose.current) {
                selectedOnClose.current = false;
                event.preventDefault();
              }
            }}
          >
            <SelectorSearch
              options={options}
              searchPlaceholder="Search loans…"
              emptyMessage="No loans found"
              onSearch={(q) => {
                setQuery(q);
                search(q);
              }}
              onSelect={(id) => {
                selectedOnClose.current = true;
                onChange(id);
                closePopover();
              }}
              onScrollEnd={handleScrollEnd}
            />
          </PopoverContent>
        </Popover>
      </FadeSwap>
    </div>
  );
};
